/* eslint-disable import/prefer-default-export */
import timezone from "dayjs/plugin/timezone";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import { isHoliday } from "@holiday-jp/holiday_jp";
import { Order, OrderCheck, OrderLargeClassification, OrderNumberStatus, OrderReceiptStatus, OrderStatus, OrderSupplier, OrderSupplierCsv, ReceiptNumberStatus, ReceiveMode, ShippingStatus, Supplier } from "../types";
import { orderStatusList, RECEIVE_MODE } from "../constants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

/**
 * 半角カタカナを全角カタカナにする
 * @param str 文字列
 */
export const narrowToWideKana = (str: string): string => {
  const convertMap = {
    ｧ: 'ァ',
    ｨ: 'ィ',
    ｩ: 'ゥ',
    ｪ: 'ェ',
    ｫ: 'ォ',
    ｬ: 'ャ',
    ｭ: 'ュ',
    ｮ: 'ョ',
    ｯ: 'ッ',
    ｰ: 'ー',
    ｱ: 'ア',
    ｲ: 'イ',
    ｳ: 'ウ',
    ｴ: 'エ',
    ｵ: 'オ',
    ｶ: 'カ',
    ｷ: 'キ',
    ｸ: 'ク',
    ｹ: 'ケ',
    ｺ: 'コ',
    ｻ: 'サ',
    ｼ: 'シ',
    ｽ: 'ス',
    ｾ: 'セ',
    ｿ: 'ソ',
    ﾀ: 'タ',
    ﾁ: 'チ',
    ﾂ: 'ツ',
    ﾃ: 'テ',
    ﾄ: 'ト',
    ﾅ: 'ナ',
    ﾆ: 'ニ',
    ﾇ: 'ヌ',
    ﾈ: 'ネ',
    ﾉ: 'ノ',
    ﾊ: 'ハ',
    ﾋ: 'ヒ',
    ﾌ: 'フ',
    ﾍ: 'ヘ',
    ﾎ: 'ホ',
    ﾏ: 'マ',
    ﾐ: 'ミ',
    ﾑ: 'ム',
    ﾒ: 'メ',
    ﾓ: 'モ',
    ﾔ: 'ヤ',
    ﾕ: 'ユ',
    ﾖ: 'ヨ',
    ﾗ: 'ラ',
    ﾘ: 'リ',
    ﾙ: 'ル',
    ﾚ: 'レ',
    ﾛ: 'ロ',
    ﾜ: 'ワ',
    ﾝ: 'ン',
    ｦ: 'ヲ',
    ｶﾞ: 'ガ',
    ｷﾞ: 'ギ',
    ｸﾞ: 'グ',
    ｹﾞ: 'ゲ',
    ｺﾞ: 'ゴ',
    ｻﾞ: 'ザ',
    ｼﾞ: 'ジ',
    ｽﾞ: 'ズ',
    ｾﾞ: 'ゼ',
    ｿﾞ: 'ゾ',
    ﾀﾞ: 'ダ',
    ﾁﾞ: 'ヂ',
    ﾂﾞ: 'ヅ',
    ﾃﾞ: 'デ',
    ﾄﾞ: 'ド',
    ﾊﾞ: 'バ',
    ﾋﾞ: 'ビ',
    ﾌﾞ: 'ブ',
    ﾍﾞ: 'ベ',
    ﾎﾞ: 'ボ',
    ﾊﾟ: 'パ',
    ﾋﾟ: 'ピ',
    ﾌﾟ: 'プ',
    ﾍﾟ: 'ペ',
    ﾎﾟ: 'ポ',
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const [narrowKana, wideKana] of Object.entries(convertMap)) {
    // eslint-disable-next-line no-param-reassign
    str = str.replace(new RegExp(narrowKana, 'g'), wideKana);
  }
  return str;
};

const toHalfWidth = (str: string) => {
  // 全角英数字を半角に変換
  // eslint-disable-next-line no-param-reassign
  str = str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
  return str;
}

/**
 * カタカナをひらがなに変換
 * @param str 文字列
 */
export const toHiragana = (str: string | null | undefined) => {
  if (str === null || str === undefined) return '';
  return narrowToWideKana(toHalfWidth(str.toLowerCase())).normalize('NFKC').replace(/[\u30a1-\u30f6]/g, (match) => {
    const char = match.charCodeAt(0) - 0x60;
    return String.fromCharCode(char);
  })
};

/**
 * ダウンロードURL
 * @param url リンクのURL
 * @param prefix fileの最初に入れる文字列
 */
export const downloadFromUrl = async (url: string, prefix: string) => {
  const link = document.createElement('a');
  const date = new Date();
  link.href = url;
  link.download = `${prefix}${date.toISOString().split('.')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * axiosによるダウンロード
 * @param url リンクのURL
 * @param prefix fileの最初に入れる文字列
 * @param onProgress ダウンロード進捗のコールバック
 */
export const axiosDownloadFromUrl = async (url: string, prefix: string, onProgress?: (percent: number | null) => void): Promise<void> => {
  // axiosでblobをダウンロード
  const response = await axios.get(url, {
    responseType: 'blob',
    onDownloadProgress: (progressEvent) => {
      // 進捗を返す
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total));
        onProgress(percentCompleted);
      }
    }
  });

  if (onProgress) onProgress(100); // ダウンロード完了時に100%を返す

  const blob = response.data;
  const blobUrl = URL.createObjectURL(blob);

  // ダウンロードリンクを作成
  const link = document.createElement('a');
  const date = new Date();
  link.href = blobUrl;
  link.download = `${prefix}${date.toISOString().split('.')[0]}.csv`;
  document.body.appendChild(link);

  // ダウンロードを実行
  link.click();

  // クリーンアップ
  document.body.removeChild(link);

  // ダウンロード開始後、少し待機してからblobUrlを解放
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 1000);
};

/**
 * 仕入先を重複無しで抽出する。
 * @param classificationList 大分類のリスト
 */
export const extractNoDupplicationSupplier = (classificationList: OrderLargeClassification[]) => {
  const sCdList: string[] = [];
  const sList: Supplier[] = [];
  classificationList.forEach(c => {
    if (c.suppliers) {
      c.suppliers.forEach((s) => {
        if (!sCdList.includes(s.supplierCd)) {
          sList.push(s);
          sCdList.push(s.supplierCd);
        }
      })
    }
  });
  return sList;
};

/**
 * ISO8601を日本時間YYYY-MM-DD HH:mmに変換する
 * @param str ISO文字列
 */
export const convertUTCToDateTimeMinutes = (str: string | null | undefined) =>
  // const dateTimeArray = str.split('T');
  // const dateStr = dateTimeArray[0].split('-').join('/');
  // const dateTimeStr = dateTimeArray[1].split(':').slice(0,2).join(':');
  // return `${dateStr} ${dateTimeStr}`;
  str ? dayjs(str).tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm') : '';

/**
 * ISO8601を日本時間のYYYY-MM-DDに変換する
 * @param str ISO文字列
 */
export const convertUTCToDate = (str: string | null | undefined) => str ? dayjs(str).tz('Asia/Tokyo').format('YYYY-MM-DD') : '';


/**
 * ゼロを区別するlocaleString
 * @param val 変換する値
 */
export const localeStringDisZero = (val: number | string | undefined | null) => {
  if (typeof val === 'number') return val.toLocaleString()
  if (typeof val === 'string') return val
  return '-';
};


/**
 * 仕入先の日時フィールドを在庫表示設定により表示するかを制御
 * @param sDate 仕入先の更新日時
 * @param sAmount 仕入先の在庫数
 */
export const checkDisplaySupplier = (sDate: string | undefined, sAmount: string | number | undefined) => {
  if (sAmount === '-') return '-'
  if (!sDate) return '-'
  return sDate;
}

/**
 * nullやundefinedを空文字に変える
 * @param val 対象の引数
 * @param defaultVal null, undefinedだった場合のデフォルトの値
 */
export const nullToBlank = (val: number | string | null | undefined, defaultVal?: string) => val == null ? (defaultVal ?? '') : val;

/**
 * nullやundefinedを0に変える
 * @param val 対象の引数
 */
export const nullToZero = (val: number | null | undefined): number => val == null ? 0 : val;

/**
 * 本部用注文オブジェクトを発注確認オブジェクトに変換
 * @param list 選択された注文
 * @returns 発注確認
 */
export const toOrderCheck = (list: Order[]): OrderCheck[] => list.map(o => {
  const {selected, ...noSelectedOrder} = o;
  return {...noSelectedOrder, error: ''};
});

/**
 * 受注画面の出荷情報設定モーダルの選択肢 => 出荷ステータス番号
 * @param shipStatus 出荷情報設定選択肢
 */
export const shippingStatusStrToNumber = (shipStatus: ShippingStatus): ReceiptNumberStatus | undefined => {
  if (shipStatus === "欠品") return 9;
  if (shipStatus === "出荷") return 4;
  if (shipStatus === "入荷待ち") return 5;
  // if (shippingStatus === "") return 3; // 空の場合は発注済に変更しない。
  return undefined;
};

/**
 * 営業日かどうかを判定する
 * @param date 判定するDayjsオブジェクト
 */
export const checkBusinessDate = (date: Dayjs): boolean => !(date.day() === 6 || date.day() === 0 || isHoliday(date.toDate()));

/**
 *
 * @param startDate
 * @param businessDate
 */
export const addBusinessDate = (startDate: Dayjs, businessDate: number) => {
  let date = startDate;
  let added = 0;
  while (added < businessDate) {
    date = date.add(1, 'day');
    if (checkBusinessDate(date)) {
      added += 1;
    }
  }
  return date;
}

/**
 * 空の場合にapiに空の情報を送るのを防ぐため、空のkey,valueは削除しておくための関数
 * @param obj 空のkey, valueを取り除くオブジェクト
 * @param requiredKeys 必須のキー
 * @param allowBlank 空文字を許可する
 * @param allowNull NULLを許可する
 */
export const removeEmptyValues = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  requiredKeys: readonly K[] = [],
  allowBlank?: boolean,
  allowNull?: boolean,
): Pick<T, K> & Partial<Omit<T, K>> => Object.entries(obj).reduce<Pick<T, K> & Partial<Omit<T, K>>>((acc, [key, value]) => {
  const isEmpty =
    value === undefined ||
    (!allowNull && value === null) ||
    (!allowBlank && typeof value === 'string' && value.trim() === '');

  if (!isEmpty || (requiredKeys as readonly (keyof T)[]).includes(key)) {
    (acc as any)[key as keyof T] = value;
  }
  return acc;
}, {} as any);

/**
 * APIのステータス番号を文字列に変更
 * @param statusNum ステータスの番号  0:未発注/1:確認中/2:発注待ち/3:発注済（出荷待ち）/4:出荷済/5:入荷待ち/9:欠品
 */
export const orderStatusFromNumber = <I extends keyof typeof orderStatusList & number>(statusNum: I): typeof orderStatusList[I] => orderStatusList[statusNum];

/**
 * APIの文字列をステータス番号に変更
 * @param statusStr ステータス
 */
export const orderStatusToNumber = (statusStr: OrderStatus): OrderNumberStatus => orderStatusList.indexOf(statusStr) as OrderNumberStatus;

/**
 * 仕入先・外部倉庫用受注オブジェクトを出荷確認オブジェクトに変換
 * @param list 選択された注文
 * @returns 発注確認
 */
export const toOrderSupplierCheck = (list: OrderSupplier[]): OrderSupplierCsv[] => list.map(o => {
  const {selected, csvDownloaded, changedShippingDate, changedExpectedShippingDate, changedArrivalDate, changedShippingCompanyName, changedTrackingNumber, changedShippingStatus, ...noSelectedOrder} = o;
  return {...noSelectedOrder, error: '',
    shippingDate: changedShippingDate,
    expectedShippingDate: changedExpectedShippingDate,
    arrivalDate: changedArrivalDate,
    shippingCompanyName: changedShippingCompanyName,
    trackingNumber: changedTrackingNumber,
    orderStatus: orderStatusFromNumber(shippingStatusStrToNumber(changedShippingStatus) ?? 4)
  };
});

/**
 * 受注ステータス候補
 * @param selectedReceivedMode 選択されている表示モード
 * @param isReceiptStatus 戻り値は受注ステータスで返すかどうか
 */
export const getReceivedStatusCandidates = (selectedReceivedMode: ReceiveMode, isReceiptStatus?: boolean) => {
  let candidates = [];
  switch (selectedReceivedMode) {
    case RECEIVE_MODE.notDownloaded:
      candidates = isReceiptStatus ? [1] : [3];
      break;
    case RECEIVE_MODE.downloaded:
      candidates = isReceiptStatus ? [1,3] : [3,5];
      break;
    case RECEIVE_MODE.shipped:
      candidates = isReceiptStatus ? [2] : [4];
      break;
    case RECEIVE_MODE.all:
      candidates = isReceiptStatus ? [1,2,3,4] : [3,4,5,9];
      break;
    default:
      candidates = isReceiptStatus ? [1,2,3,4] : [3,4,5,9];
      break;
  }
  return candidates;
}

/**
 * 受注ステータスのパラメータを返す
 * @param selectedReceivedMode 受注モード
 * @param orderReceiptStatus   受注ステータス
 */
export const getReceivedStatusParam = (selectedReceivedMode: ReceiveMode, orderReceiptStatus: OrderReceiptStatus): `${OrderNumberStatus}` | string => {
  const candidates = getReceivedStatusCandidates(selectedReceivedMode, false);
  if (orderReceiptStatus && candidates.includes(orderStatusToNumber(orderReceiptStatus))) {
    return `${orderStatusToNumber(orderReceiptStatus)}`;
  }
  return candidates.join(',') as string;
}

/**
 * 要素順、indexを共に逆転させてmapを実行する
 * @param xs 配列
 * @param f 実行関数
 */
export const mapReversedElements = <T, R>(xs: readonly T[] | T[], f: (v: T, rIndex: number) => R): R[] => [...xs].reverse().map((v,i,a) => f(v, a.length - 1 - i));
